import { Component, computed, inject, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { lastValueFrom, map } from 'rxjs';
import { AssignmentWithRelations } from '../../../api/app-types/assignment';
import { DB_PHASES, PHASE_TRANSITION_TYPES } from '../../../api/app-types/phase.app';
import { REPORT_TYPE, ReportDocument } from '../../../api/app-types/report-document.app';
import { ValidationPhaseResponse } from '../../../api/app-types/validation.app';
import { ASSIGNMENT_API, provideAssignmentApi } from '../../../api/services/assignment.service';
import { AssignmentChecks } from '../../../core/components/assignment-checks/assignment-checks';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { STATE } from '../../../core/services/state.service';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { determinePhaseTransitionType } from '../../../shared/helpers/assignment.func';
import { AdditionalAssignmentDetails } from '../../client/client-assignments/additional-assignment/additional-assignment-details/additional-assignment-details';
import { AssignmentLinks } from '../../../core/components/assignment-links/assignment-links';
import { LOGGER } from '../../../shared/logging/log.service';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { MatDialog } from '@angular/material/dialog';
import { GenericDialog } from '../../../core/components/generic-dialog/generic-dialog';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-additional-assignment-move',
	templateUrl: './additional-assignment-move.html',
	imports: [
		TranslocoPipe,
		LvrPaddingDirective,
		AssignmentChecks,
		LvrDefaultActions,
		AdditionalAssignmentDetails,
		AssignmentLinks,
	],
	providers: [FormPersistenceService, provideAssignmentApi],
})
export class AdditionalAssignmentMove extends RouteInjection {
	private readonly transloco = inject(TranslocoService);
	private readonly activeRoute = inject(ActivatedRoute);
	private readonly api = inject(ASSIGNMENT_API);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly state = inject(STATE);
	private readonly draftService = inject(FormPersistenceService);
	private readonly dialog = inject(MatDialog);
	private readonly session = inject(SessionManagerService);
	public readonly currentUrl = this.router.url;
	private readonly data = this.resolveDataAsSignal<{
		assignment?: AssignmentWithRelations;
		validationErrors: ValidationPhaseResponse;
	}>('assignmentData');
	private readonly reportsPage = this.resolveDataAsSignal<PaginatedResponse<ReportDocument>>('reports');
	private readonly logger = inject(LOGGER);
	public readonly assignment = computed(() => this.data()?.assignment);
	public readonly validationData = computed(() => this.data()?.validationErrors);
	public readonly validationErrors = computed(() => {
		const errors = this.data()?.validationErrors;
		if (!errors) {
			return [];
		}
		const errs = Object.keys(errors)
			.map((key) => errors[key])
			.flat();

		return errs.filter((err) => err.severity.toLowerCase() === 'error');
	});
	public readonly clientId: Signal<number | string> = toSignal(
		this.activeRoute.data.pipe(map((data) => data['clientId'])),
	);
	private readonly assignmentId: Signal<number | string> = computed(() => this.assignment()!.id);
	public readonly targetPhase = computed(() => Number(this.assignment()?.phaseChangeId ?? DB_PHASES.P4) as DB_PHASES);
	public readonly shouldBeEndReport = computed(() => this.targetPhase() === DB_PHASES.P4);
	public readonly phaseTransitionType = computed(() => determinePhaseTransitionType(this.assignment()));
	public readonly reportIdsByType = computed(() => {
		const reports = this.reportsPage()?.entities ?? [];
		return {
			start: reports.find((r) => r.categoryReport.id === REPORT_TYPE.START_REPORT)?.id,
			impediment: reports.find((r) => r.categoryReport.id === REPORT_TYPE.IMPEDIMENT_REPORT)?.id,
			end: reports.find((r) => r.categoryReport.id === REPORT_TYPE.END_REPORT)?.id,
		};
	});
	private readonly isDraftImpedimentReport = computed(() => {
		const reports = this.reportsPage()?.entities ?? [];
		const report = reports.find((r) => r.categoryReport.id === REPORT_TYPE.IMPEDIMENT_REPORT);

		return report ? !report.isFinal : false;
	});
	private readonly canTransitionPhases = hasPermission(this.session.permissions, 'Phases.Transition');
	public readonly isPhase4Transition = computed(
		() =>
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P0_TO_P4 ||
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P1_TO_P4 ||
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P2_TO_P4 ||
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P3_TO_P4,
	);
	public readonly isPhase3Transition = computed(
		() =>
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P1_TO_P3 ||
			this.phaseTransitionType() === PHASE_TRANSITION_TYPES.P2_TO_P3,
	);
	public readonly saveButtonKey = computed(() => {
		if (this.isPhase3Transition()) return 'assignment.phase.save_register';
		if (this.targetPhase() === DB_PHASES.P4) return 'assignment.phase.save_cancel';
		return 'assignment.phase.save_switch';
	});
	public readonly saving = signal<boolean>(false);
	public readonly disableContinueButton = computed(() => {
		if (!this.canTransitionPhases()) return true;
		const errors = this.validationErrors();
		if (this.targetPhase() === DB_PHASES.P4 && this.isPhase4Transition() && errors.length === 0) {
			return this.saving();
		}
		if (this.saving() || errors.length > 0) return true;
		return false;
	});

	private async confirmImpedimentReportDeletion() {
		if (!this.isDraftImpedimentReport()) return true;

		const confirmation = await lastValueFrom(
			this.dialog
				.open(GenericDialog, {
					data: {
						titleKey: 'assignment.impediment_report_delete_dialog.title',
						messageKey: 'assignment.impediment_report_delete_dialog.message',
						confirmKey: 'assignment.impediment_report_delete_dialog.confirm',
						cancelKey: 'assignment.impediment_report_delete_dialog.cancel',
						iconConfirm: 'arrow_forward',
					},
					maxWidth: '600px',
				})
				.afterClosed(),
		);

		return !!confirmation;
	}

	public async save() {
		if (this.saving()) return;

		this.saving.set(true);
		try {
			const confirmed = await this.confirmImpedimentReportDeletion();

			if (!confirmed) {
				return;
			}

			const target = this.targetPhase() || DB_PHASES.P4;
			await this.api.moveAdditionalAssignment(this.clientId(), this.assignmentId(), target);
			await this.state.updateAssignments();
			const successKey = 'base.notification_message.additional_assignment_assigned';
			this.ntfy.addNotification(createSuccessNotification(this.transloco.translate(successKey)));
			this.draftService.deleteDraft('phase', this.assignmentId()!.toString());

			const assignments = this.state.currentAssignments();
			const hasAdditionalAssignments = assignments.some(
				(assignment) => assignment.isAdditionalAssignment || (assignment.additionalAssignments?.length ?? 0) > 0,
			);

			// If the user no longer has any additional assignments, navigate to the dashboard
			if (!hasAdditionalAssignments) {
				await this.router.navigate(['/']);
			} else {
				await this.router.navigate(['../../'], { relativeTo: this.activeRoute });
			}
		} catch (error) {
			const errorMsg = this.transloco.translate('base.notification_message.additional_assignment_assign_error');
			this.logger.error(error as Error);
			this.ntfy.addNotification(createErrorNotification(errorMsg));
		} finally {
			this.saving.set(false);
		}
	}

	public async cancel() {
		await this.api.startPhaseTransition(this.clientId(), this.assignmentId(), -1);
		await this.state.updateAssignments();
		this.draftService.deleteDraft('phase', this.assignmentId()!.toString());
		await this.router.navigate(['../../'], { relativeTo: this.activeRoute });
	}
}
